import { useState, type JSX } from 'react';
import { NumberField } from '../../components/NumberField';
import { Slider } from '../../components/Slider';
import { useActions, useProject, useRuntime, useUi, useUiContext } from '../../context';
import { attempt, safely } from '../../safe';
import type { ThemePreference } from '../../theme/theme';
import type { EditorPrefs } from '../../uiStore';
import { DevicePicker } from '../properties/DeviceField';
import { Limitations } from '../misc/limitations';
import { CommitInput } from './CommitInput';
import { FPS_PRESETS, matchResolutionPreset, RESOLUTION_PRESETS, sanitizeDimension, sanitizeFps } from './settingsMath';

function Check({ label, checked, onChange }: { label: string; checked: boolean; onChange(value: boolean): void }): JSX.Element {
  return (
    <label className="ui-check" style={{ display: 'flex', padding: '3px 0' }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

type AlertPermission = NotificationPermission | 'unsupported';

const currentAlertPermission = (embedded: boolean): AlertPermission => {
  if (embedded) return 'unsupported';
  try {
    return typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
  } catch {
    return 'unsupported';
  }
};

const ALERT_STATE: Record<AlertPermission, string> = {
  granted: 'On: a system notification appears if the stream has a problem while this tab is in the background.',
  denied: 'Blocked by the browser. Allow notifications for this site in the browser settings to turn them on.',
  default: 'Off. Stream problems are shown in the studio only.',
  unsupported: 'System notifications are not available here (this browser has none, or the studio is embedded in another page).',
};

/** System notifications for stream problems: where things stand and the way to turn them on. */
function AlertSettings(): JSX.Element {
  const { studio, ui } = useUiContext();
  const offer = useUi((s) => s.prefs.offerAlerts);
  const embedded = useUi((s) => s.embedded);
  const [permission, setPermission] = useState<AlertPermission>(() => currentAlertPermission(embedded));
  return (
    <>
      <div className="ui-section-title">Stream problem alerts</div>
      <div className="ui-row" style={{ alignItems: 'flex-start' }}>
        <span className="ui-grow" role="status" aria-label="System notifications" data-permission={permission}>
          {ALERT_STATE[permission]}
        </span>
        <button
          type="button"
          className="ui-btn"
          disabled={permission !== 'default'}
          onClick={() =>
            void safely(studio, async () => {
              setPermission(await studio.requestAlertPermission());
            }, 'Could not ask for notification permission')
          }
        >
          Enable system notifications
        </button>
      </div>
      <Check label="Offer to enable them when I start streaming or recording" checked={offer} onChange={(offerAlerts) => ui.getState().setPrefs({ offerAlerts })} />
    </>
  );
}

export function GeneralTab(): JSX.Element {
  const { studio, ui } = useUiContext();
  const prefs = useUi((s) => s.prefs);
  const themeOverridden = useUi((s) => s.themeHash !== null || s.themeParent !== null);
  const name = useProject((p) => p.name);
  const set = (patch: Partial<EditorPrefs>): void => ui.getState().setPrefs(patch);
  return (
    <div>
      <div className="ui-section-title">Scene collection</div>
      <div className="ui-field">
        <label htmlFor="settings-collection-name">Name</label>
        <CommitInput
          id="settings-collection-name"
          label="Scene collection name"
          value={name}
          onCommit={(value) => value.trim() && attempt(studio, () => studio.commands.renameProject(value.trim()))}
        />
      </div>

      <div className="ui-section-title">Appearance</div>
      <div className="ui-field">
        <label htmlFor="settings-theme">Theme</label>
        <select
          id="settings-theme"
          className="ui-select"
          value={prefs.theme}
          onChange={(e) => set({ theme: e.target.value as ThemePreference })}
        >
          <option value="system">Match the system</option>
          <option value="dark">Dark</option>
          <option value="light">Light</option>
        </select>
      </div>
      {themeOverridden ? <p className="ui-dim">The page that opened the studio chose its theme; this preference applies when the studio is opened on its own.</p> : null}

      <div className="ui-section-title">Snapping</div>
      <Check label="Enable snapping in the preview editor" checked={prefs.snapEnabled} onChange={(snapEnabled) => set({ snapEnabled })} />
      <Check label="Snap to the edges of the canvas" checked={prefs.snapToCanvas} onChange={(snapToCanvas) => set({ snapToCanvas })} />
      <Check label="Snap to the centre lines" checked={prefs.snapToCenter} onChange={(snapToCenter) => set({ snapToCenter })} />
      <Check label="Snap to other items" checked={prefs.snapToItems} onChange={(snapToItems) => set({ snapToItems })} />
      <div className="ui-field">
        <span className="ui-field-label">Snap sensitivity</span>
        <div className="ui-row">
          <Slider
            label="Snap sensitivity in pixels"
            value={prefs.snapDistance}
            min={1}
            max={50}
            step={1}
            valueText={`${prefs.snapDistance} pixels`}
            disabled={!prefs.snapEnabled}
            onChange={(snapDistance) => set({ snapDistance })}
          />
          <span className="ui-mono">{prefs.snapDistance} px</span>
        </div>
      </div>

      <div className="ui-section-title">Confirmations</div>
      <Check label="Ask before removing a scene" checked={prefs.confirmRemoveScene} onChange={(confirmRemoveScene) => set({ confirmRemoveScene })} />
      <Check label="Ask before removing items" checked={prefs.confirmRemoveItem} onChange={(confirmRemoveItem) => set({ confirmRemoveItem })} />
      <Check label="Ask before stopping the stream" checked={prefs.confirmStopStream} onChange={(confirmStopStream) => set({ confirmStopStream })} />
      <Check label="Ask before stopping a recording" checked={prefs.confirmStopRecording} onChange={(confirmStopRecording) => set({ confirmStopRecording })} />
      <AlertSettings />
      <div className="ui-section-title">Diagnostics</div>
      <Check
        label="Diagnostics mode: offer test sources (test signal) in Add Source"
        checked={prefs.diagnosticsMode}
        onChange={(diagnosticsMode) => set({ diagnosticsMode })}
      />
      <p className="ui-dim">Development builds and the ?diagnostics=1 address parameter turn it on regardless; ?diagnostics=0 turns it off.</p>
      <p className="ui-dim">Theme, snapping, confirmation, alert and diagnostics preferences are stored in this browser, not in the scene collection.</p>
    </div>
  );
}

export function VideoTab(): JSX.Element {
  const { studio } = useUiContext();
  const actions = useActions();
  const video = useProject((p) => p.video);
  // Only the output state matters here; reading it through the runtime keeps this reactive.
  useRuntime((r) => `${r.broadcast.status}|${r.whip.status}|${r.swarm.status}|${r.recording.status}`);
  const [draft, setDraft] = useState(video);
  const [customSize, setCustomSize] = useState(matchResolutionPreset(video.width, video.height) === 'custom');
  const [customFps, setCustomFps] = useState(!FPS_PRESETS.includes(video.fps));

  const outputsActive = actions.isStreaming() || actions.isRecording();
  const dirty = draft.width !== video.width || draft.height !== video.height || draft.fps !== video.fps;
  const preset = customSize ? 'custom' : matchResolutionPreset(draft.width, draft.height);

  const apply = (): void => {
    const next = { width: sanitizeDimension(draft.width), height: sanitizeDimension(draft.height), fps: sanitizeFps(draft.fps) };
    setDraft(next);
    attempt(studio, () => studio.commands.setVideoSettings(next));
  };

  return (
    <div>
      <div className="ui-section-title">Canvas</div>
      <div className="ui-field">
        <label htmlFor="settings-resolution">Base resolution</label>
        <select
          id="settings-resolution"
          className="ui-select"
          value={preset}
          onChange={(e) => {
            const found = RESOLUTION_PRESETS.find((p) => p.id === e.target.value);
            setCustomSize(!found);
            if (found) setDraft({ ...draft, width: found.width, height: found.height });
          }}
        >
          {RESOLUTION_PRESETS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
          <option value="custom">Custom…</option>
        </select>
        {preset === 'custom' ? (
          <>
            <span className="ui-field-label">Custom size</span>
            <div className="ui-row">
              <NumberField label="Canvas width" value={draft.width} min={16} max={7680} step={2} unit="×" precision={0} onChange={(width) => setDraft({ ...draft, width })} />
              <NumberField label="Canvas height" value={draft.height} min={16} max={7680} step={2} unit="px" precision={0} onChange={(height) => setDraft({ ...draft, height })} />
            </div>
          </>
        ) : null}
      </div>
      <div className="ui-field">
        <label htmlFor="settings-fps">Frame rate</label>
        <select
          id="settings-fps"
          className="ui-select"
          value={customFps ? 'custom' : String(draft.fps)}
          onChange={(e) => {
            const custom = e.target.value === 'custom';
            setCustomFps(custom);
            if (!custom) setDraft({ ...draft, fps: Number(e.target.value) });
          }}
        >
          {FPS_PRESETS.map((fps) => (
            <option key={fps} value={fps}>
              {fps} fps
            </option>
          ))}
          <option value="custom">Custom…</option>
        </select>
        {customFps ? (
          <>
            <span className="ui-field-label">Custom rate</span>
            <NumberField label="Frames per second" value={draft.fps} min={1} max={120} step={1} unit="fps" precision={0} onChange={(fps) => setDraft({ ...draft, fps })} />
          </>
        ) : null}
      </div>
      <div className="ui-row" style={{ marginTop: 12 }}>
        <button type="button" className="ui-btn primary" disabled={!dirty || outputsActive} onClick={apply}>
          Apply
        </button>
        <button type="button" className="ui-btn" disabled={!dirty} onClick={() => setDraft(video)}>
          Revert
        </button>
        {outputsActive ? <span className="ui-warn-text">Stop streaming and recording before changing the canvas.</span> : null}
      </div>
      <p className="ui-dim">
        Items keep their pixel positions when the canvas size changes. Under sustained overload the renderer lowers its internal
        resolution automatically; the output size stays the same.
      </p>
    </div>
  );
}

export function AudioTab(): JSX.Element {
  const { studio } = useUiContext();
  const audio = useProject((p) => p.audio);
  const contextState = useRuntime((r) => r.audioContextState);
  const sinkSupported = typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;
  return (
    <div>
      <div className="ui-section-title">Audio engine</div>
      <div className="ui-field">
        <label htmlFor="settings-sample-rate">Sample rate</label>
        <select
          id="settings-sample-rate"
          className="ui-select"
          value={audio.sampleRate}
          onChange={(e) => attempt(studio, () => studio.commands.setAudioSettings({ sampleRate: Number(e.target.value) === 44100 ? 44100 : 48000 }))}
        >
          <option value={48000}>48 kHz</option>
          <option value={44100}>44.1 kHz</option>
        </select>
        <span className="ui-field-help">
          The audio engine is created once per session, so a new sample rate takes effect after the page is reloaded. Engine state:{' '}
          {contextState}.
        </span>
      </div>
      <div className="ui-section-title">Monitoring</div>
      <div className="ui-field">
        <span className="ui-field-label">Monitoring device</span>
        <DevicePicker
          kind="audiooutput"
          label="Monitoring device"
          value={audio.monitorDeviceId ?? ''}
          onChange={(deviceId) => attempt(studio, () => studio.commands.setAudioSettings({ monitorDeviceId: deviceId || null }))}
        />
        {!sinkSupported ? (
          <span className="ui-field-help ui-warn-text">This browser cannot route audio to a chosen output device; monitoring uses the default output.</span>
        ) : null}
      </div>
    </div>
  );
}

export function AboutTab(): JSX.Element {
  return (
    <div>
      <div className="ui-section-title">Browser limitations</div>
      <Limitations />
    </div>
  );
}
